import { useState, useEffect } from 'react';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('declaration');
  const [prefs, setPrefs] = useState({
    necessary: true,
    performance: false,
    targeting: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cv_cookie_consent');
    if (!consent) setTimeout(() => setVisible(true), 1500);
  }, []);

  const acceptAll = () => {
    setPrefs({ necessary: true, performance: true, targeting: true });
    localStorage.setItem('cv_cookie_consent', 'all');
    setVisible(false);
    setShowSettings(false);
  };

  const declineAll = () => {
    setPrefs({ necessary: true, performance: false, targeting: false });
    localStorage.setItem('cv_cookie_consent', 'necessary');
    setVisible(false);
    setShowSettings(false);
  };

  const savePrefs = () => {
    localStorage.setItem('cv_cookie_consent', JSON.stringify(prefs));
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[998] bg-black/70 dark:bg-white/70 backdrop-blur-sm"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center px-4">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-0">
              <h2 className="text-white text-lg" style={{fontFamily:'Anton,sans-serif',fontWeight:500,letterSpacing:'0.02em'}}>COOKIE SETTINGS</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                aria-label="Close settings"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 mx-6 mt-5 bg-white/5 dark:bg-white/5 rounded-xl p-1 border border-white/10">
              <button
                onClick={() => setActiveTab('declaration')}
                className={`flex-1 py-2.5 text-xs font-medium tracking-widest uppercase rounded-lg transition-all duration-200 ${
                  activeTab === 'declaration'
                    ? 'bg-white dark:bg-[#111] text-black dark:text-white'
                    : 'text-white/40 hover:text-white'
                }`}
              >
                Cookie Declaration
              </button>
              <button
                onClick={() => setActiveTab('about')}
                className={`flex-1 py-2.5 text-xs font-medium tracking-widest uppercase rounded-lg transition-all duration-200 ${
                  activeTab === 'about'
                    ? 'bg-white dark:bg-[#111] text-black dark:text-white'
                    : 'text-white/40 hover:text-white'
                }`}
              >
                About Cookies
              </button>
            </div>

            {/* Tab Content */}
            <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-4">

              {activeTab === 'declaration' && (
                <>
                  {/* Necessary */}
                  <div className="bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-white font-medium text-sm">Strictly Necessary</h3>
                        <p className="text-white/30 text-xs mt-0.5">Always active</p>
                      </div>
                      {/* Always ON — not clickable but shows ON state */}
                      <div className="w-11 h-6 bg-white dark:bg-[#111] rounded-full relative flex-shrink-0 cursor-not-allowed">
                        <div className="w-4 h-4 bg-black rounded-full absolute top-1 right-1 transition-all duration-200" />
                      </div>
                    </div>
                    <p className="text-xs text-white/40 leading-relaxed">
                      These cookies are essential for the website to function. They enable core functionality such as page navigation, secure login, and access to website features. The website cannot work properly without these cookies.
                    </p>
                  </div>

                  {/* Performance */}
                  <div className="bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-white font-medium text-sm">Performance</h3>
                        <p className="text-white/30 text-xs mt-0.5">Optional</p>
                      </div>
                      <button
                        onClick={() => setPrefs(p => ({ ...p, performance: !p.performance }))}
                        className={`w-11 h-6 rounded-full relative flex-shrink-0 transition-colors duration-200 ${prefs.performance ? 'bg-white dark:bg-[#111]' : 'bg-white/20'}`}
                        aria-label="Toggle performance cookies"
                      >
                        <div className={`w-4 h-4 rounded-full absolute top-1 transition-all duration-200 ${prefs.performance ? 'bg-black right-1' : 'bg-white dark:bg-[#111] left-1'}`} />
                      </button>
                    </div>
                    <p className="text-xs text-white/40 leading-relaxed">
                      Performance cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve how our website works.
                    </p>
                  </div>

                  {/* Targeting */}
                  <div className="bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-white font-medium text-sm">Targeting</h3>
                        <p className="text-white/30 text-xs mt-0.5">Optional</p>
                      </div>
                      <button
                        onClick={() => setPrefs(p => ({ ...p, targeting: !p.targeting }))}
                        className={`w-11 h-6 rounded-full relative flex-shrink-0 transition-colors duration-200 ${prefs.targeting ? 'bg-white dark:bg-[#111]' : 'bg-white/20'}`}
                        aria-label="Toggle targeting cookies"
                      >
                        <div className={`w-4 h-4 rounded-full absolute top-1 transition-all duration-200 ${prefs.targeting ? 'bg-black right-1' : 'bg-white dark:bg-[#111] left-1'}`} />
                      </button>
                    </div>
                    <p className="text-xs text-white/40 leading-relaxed">
                      Targeting cookies are used to identify visitors and build a profile of interests to show relevant advertising across websites. They remember your visit and share this information with advertisers.
                    </p>
                  </div>
                </>
              )}

              {activeTab === 'about' && (
                <div className="flex flex-col gap-4">
                  <div className="bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-medium text-sm mb-3">What are cookies?</h3>
                    <p className="text-xs text-white/40 leading-relaxed">
                      Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
                    </p>
                  </div>
                  <div className="bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-medium text-sm mb-3">How do we use cookies?</h3>
                    <p className="text-xs text-white/40 leading-relaxed">
                      CodeViora uses cookies to improve your browsing experience, analyze website traffic, and personalize content. We only use cookies that are necessary for the website to function unless you give us permission to use optional cookies.
                    </p>
                  </div>
                  <div className="bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-medium text-sm mb-3">Your choices</h3>
                    <p className="text-xs text-white/40 leading-relaxed">
                      You can choose to accept or decline optional cookies. You can also change your preferences at any time. Please note that declining some cookies may affect your experience on our website.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 p-6 border-t border-white/10">
              <button
                onClick={declineAll}
                className="flex-1 border border-white/20 text-white/60 text-xs font-medium py-3 rounded-xl hover:border-white hover:text-white transition-colors"
              >
                DECLINE
              </button>
              <button
                onClick={savePrefs}
                className="flex-1 border border-white text-white text-xs font-medium py-3 rounded-xl hover:bg-white/10 transition-colors"
              >
                SAVE
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 bg-white dark:bg-[#111] text-black dark:text-white text-xs font-medium py-3 rounded-xl hover:bg-white/90 transition-colors"
              >
                ACCEPT ALL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Banner */}
      {!showSettings && (
        <div className="fixed bottom-4 left-4 z-[997] w-80 bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl p-6 animate-fade-up">
          {/* Close */}
          <button
            onClick={declineAll}
            className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/50 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Cookie Icon */}
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
            <span className="text-xl">🍪</span>
          </div>

          <h3 className="text-white text-base mb-2 pr-8" style={{fontFamily:'Anton,sans-serif',fontWeight:500,letterSpacing:'0.02em'}}>
            WE USE COOKIES
          </h3>
          <p className="text-xs text-white/40 leading-relaxed mb-5">
            We use cookies to enhance your browsing experience and analyze site traffic. You can choose which cookies to allow.
          </p>

          <div className="flex flex-col gap-2">
            <button
              onClick={acceptAll}
              className="w-full bg-white dark:bg-[#111] text-black dark:text-white text-xs font-medium py-3 rounded-xl hover:bg-white/90 transition-colors"
            >
              ACCEPT ALL
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={declineAll}
                className="border border-white/20 text-white/60 text-xs font-medium py-2.5 rounded-xl hover:border-white hover:text-white transition-colors"
              >
                DECLINE
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="border border-white/20 text-white/60 text-xs font-medium py-2.5 rounded-xl hover:border-white hover:text-white transition-colors"
              >
                SETTINGS
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}